'use client';

// Production collar hedger — position-level Greeks, sizing, stress, roll
// triggers, and tax flags. Mounted inside the Hedge Lab focused view
// whenever the user selects the COLLAR strategy.
//
// Inputs: a HedgeStrategy (the collar with its chosen legs) + the holding's
// spot + weight in book. The user supplies the actual share count (default
// 10,000 as a sensible starting point — production should pull from a real
// AUM-aware computation; portfolio.json stores normalized fractions, not
// integer share counts).

import { useMemo, useState } from 'react';
import { useResource, invalidateResource } from '@/lib/use-resource';
import type { HedgeStrategy } from '@/lib/_hedge/types';
import { TradeTicket, GreekCell, SummaryCell, StressMatrix } from './HedgeRenderHelpers';
import {
  buildPositionGreeks,
  solveContractsForTargetDelta,
  buildDeltaDriftTable,
  CONTRACT_MULTIPLIER,
} from '@/lib/_hedge/greeks';
import {
  recommendHedgeRatio,
  assessConstructiveSaleRisk,
  summarizeCanadianTaxTreatment,
  extractCollarStrikes,
} from '@/lib/_hedge/sizing';
import {
  buildStressMatrix,
  assessRollTriggers,
} from '@/lib/_hedge/stress';

interface Props {
  symbol: string;
  spot: number;
  weightPct: number;
  strategy: HedgeStrategy;
  /** Days the underlying has been held — drives §1259 / §1092 logic. */
  daysHeld?: number;
  /** Decimal — 0.20 = 20% unrealized gain. Drives §1259 escalation. */
  unrealizedGainPct?: number | null;
  /** ISO timestamp of the options-chain JSON the strategy was built against.
   *  Surfaced as a chain-prices freshness label so the trade ticket isn't
   *  read as live during market hours when the underlying data is from the
   *  pre-market 6 AM SAPI pull. */
  chainAsOf?: string | null;
}

interface PortfolioApi {
  shares?: Record<string, number>;
  bookNav?: number;
}

const fmtUsd = (n: number) =>
  `${n >= 0 ? '+' : '−'}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtUsdNoSign = (n: number) =>
  `$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtSignedPct = (n: number) => `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}%`;
const fmtDelta = (n: number) =>
  `${n >= 0 ? '+' : '−'}${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

export function ProductionHedger({
  symbol,
  spot,
  weightPct,
  strategy,
  daysHeld = 400,
  unrealizedGainPct = null,
  chainAsOf = null,
}: Props) {
  // Real position size from portfolio.json (shares × bookNav). When this
  // symbol isn't in the book — or portfolio isn't loaded yet — we fall back
  // to a sensible 10K default so the page renders. The user can override the
  // count via the input box; the "Use my position" pill resets it.
  const { data: portfolio } = useResource<PortfolioApi>('/api/portfolio');
  const realShares = useMemo(() => {
    const normalized = portfolio?.shares?.[symbol];
    const nav = portfolio?.bookNav ?? 1_000_000;
    if (typeof normalized !== 'number' || normalized <= 0 || !Number.isFinite(nav) || nav <= 0) {
      return null;
    }
    return Math.round(normalized * nav);
  }, [portfolio, symbol]);

  const [shares, setShares] = useState(10_000);
  const [hedgeRatio, setHedgeRatio] = useState<number>(1.0); // 1.0 = 1:1 coverage
  const [eventDriven, setEventDriven] = useState(false);
  const [refreshingChain, setRefreshingChain] = useState(false);
  const [chainRefreshError, setChainRefreshError] = useState<string | null>(null);

  async function handleRefreshChain() {
    setRefreshingChain(true);
    setChainRefreshError(null);
    try {
      const res = await fetch(`/api/refresh-options-chain?symbol=${encodeURIComponent(symbol)}`, { method: 'POST' });
      const data = await res.json();
      if (!data?.ok) {
        setChainRefreshError(data?.error ?? 'refresh failed');
        return;
      }
      // Drop the cached /api/hedge-lab payload + reload so the freshly fetched
      // chain is used for strategy construction and the trade-ticket bid/ask.
      invalidateResource('/api/hedge-lab');
      if (typeof window !== 'undefined') window.location.reload();
    } catch (e) {
      setChainRefreshError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshingChain(false);
    }
  }

  // First time realShares resolves, snap the input to it (only if user has
  // not already typed a custom value).
  const [touched, setTouched] = useState(false);
  if (!touched && realShares != null && shares === 10_000) {
    setShares(realShares);
  }

  // Pull strikes + per-share Greeks off the strategy legs. Builders now
  // attach gamma/vega/theta from the chain row so we have what we need
  // without re-fetching.
  const { putStrike, callStrike, callDaysToExpiry } = useMemo(
    () => extractCollarStrikes(strategy.legs),
    [strategy.legs],
  );
  const daysToExpiry = strategy.daysToExpiry ?? callDaysToExpiry ?? 39;

  const perShareGreeks = useMemo(() => {
    return strategy.legs.map(l => {
      if (l.asset === 'stock') return null;
      if (l.delta == null || l.gamma == null || l.vega == null || l.theta == null) return null;
      return { delta: l.delta, gamma: l.gamma, vega: l.vega, theta: l.theta };
    });
  }, [strategy.legs]);

  // hedgeRatio is "fraction of stock-delta to cover". 1.0 = standard 1:1
  // coverage (one contract pair per 100 shares). The math:
  //   coverage = hedgeRatio means contracts = (stockDelta × hedgeRatio) / 100
  // For partial / over-hedges we use the delta solver to land on the right
  // target delta:
  //   targetDeltaFraction = 1 − hedgeRatio
  //   contracts = (targetDelta − shares) / perContractOptionDelta
  const contracts = useMemo(() => {
    if (hedgeRatio <= 0) return 0;
    // Standard 1:1 coverage: contracts = shares × hedgeRatio / 100
    // This is what 99% of production hedgers actually trade — one pair per 100
    // shares, scaled by the % you want to hedge. The solver mode (delta-neutral
    // over-hedge) is a power-user feature; default to 1:1 for the slider.
    return Math.round((shares * hedgeRatio) / CONTRACT_MULTIPLIER);
  }, [shares, hedgeRatio]);

  // Recommended hedge ratio based on tax + concentration + horizon.
  const recommendation = useMemo(() => recommendHedgeRatio({
    daysHeld,
    weight: weightPct / 100,
    eventDriven,
    unrealizedGainPct,
  }), [daysHeld, weightPct, eventDriven, unrealizedGainPct]);

  // Position Greeks at the chosen contract count.
  const greeks = useMemo(() => buildPositionGreeks({
    shares,
    spot,
    legs: strategy.legs.filter(l => l.asset !== 'stock'),
    contracts,
    perShareGreeks: perShareGreeks.filter((_, i) => strategy.legs[i].asset !== 'stock'),
  }), [shares, spot, strategy.legs, contracts, perShareGreeks]);

  // Greeks if we went fully delta-neutral (solver mode) — surfaced as a
  // reference even when the user is on the 1:1 slider.
  const neutralContracts = useMemo(() => {
    const optionLegs = strategy.legs.filter(l => l.asset !== 'stock');
    const optionDeltas = strategy.legs
      .filter(l => l.asset !== 'stock')
      .map(l => l.delta ?? null);
    return solveContractsForTargetDelta({
      shares,
      legs: optionLegs,
      perShareDeltas: optionDeltas,
      targetDeltaFraction: 0,
    });
  }, [shares, strategy.legs]);

  // Canadian tax assessment — §80.6 ITA Synthetic Disposition + capital
  // gains 50% inclusion summary. (US §1092 QCC rules don't apply.)
  const cs = (putStrike != null && callStrike != null) ? assessConstructiveSaleRisk({
    spot, putStrike, callStrike, unrealizedGainPct,
  }) : null;

  // Position-level premium $$.
  const callLeg = strategy.legs.find(l => l.asset === 'call' && l.action === 'sell');
  const putLeg = strategy.legs.find(l => l.asset === 'put' && l.action === 'buy');
  const callPriceUnit = callLeg?.bid ?? callLeg?.pricePerUnit ?? 0;
  const putPriceUnit = putLeg?.ask ?? putLeg?.pricePerUnit ?? 0;
  const callPremium = callPriceUnit * CONTRACT_MULTIPLIER * contracts;
  const putCost = putPriceUnit * CONTRACT_MULTIPLIER * contracts;
  const netPremium = callPremium - putCost;

  // Canadian tax summary using actual position-level dollar premiums.
  const canadianTax = (putStrike != null && callStrike != null) ? summarizeCanadianTaxTreatment({
    spot, putStrike, callStrike, callPremium: callPriceUnit, putCost: putPriceUnit, daysToExpiry,
  }) : null;

  // Stress matrix.
  const stress = useMemo(() => buildStressMatrix(greeks, daysToExpiry), [greeks, daysToExpiry]);

  // Delta drift.
  const drift = useMemo(() => buildDeltaDriftTable(greeks), [greeks]);

  // Roll triggers (using the current spot + entry IV from the legs).
  // entryIv pulled inside the useMemo body so the React Compiler doesn't
  // complain about a mutable closure dep.
  const triggers = useMemo(() => {
    const entryIv = callLeg?.iv ?? null;
    return assessRollTriggers({
      spot,
      shortCallStrike: callStrike,
      longPutStrike: putStrike,
      entryIv,
      currentIv: entryIv, // same since we're building it at trade-on
      shareDeltaCurrent: greeks.total.shareDelta,
      shareDeltaTarget: greeks.total.shareDelta,
      stockShares: shares,
    });
  }, [spot, callStrike, putStrike, callLeg, greeks.total.shareDelta, shares]);

  if (putStrike == null || callStrike == null) {
    return (
      <div className="border border-amber-500/30 rounded-lg bg-amber-500/5 p-4 text-[11px] text-amber-200">
        Production hedger requires a collar with both put and call legs.
      </div>
    );
  }

  return (
    <section className="border border-amber-500/40 rounded-lg bg-[#0d1117] p-4 space-y-4">
      <header className="flex items-baseline justify-between gap-3 border-b border-amber-500/20 pb-2">
        <div>
          <span className="text-[14px] font-semibold text-amber-300 uppercase tracking-wide">Production Hedger</span>
          <span className="ml-3 text-[10px] text-[#64748b]">{symbol} collar · {daysToExpiry}d tenor · ${putStrike}P / ${callStrike}C</span>
        </div>
        <div className="flex items-baseline gap-3">
          {chainAsOf && (
            <span
              className="text-[9px] text-amber-400/70 uppercase tracking-wider"
              title="Option-chain bid/ask values are sourced from data/options-chain.json. Click 'refresh chain' to pull the live SAPI chain for this symbol now (~15-25s)."
            >
              chain as of {new Date(chainAsOf).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          <button
            type="button"
            onClick={handleRefreshChain}
            disabled={refreshingChain}
            className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/70 disabled:opacity-40 disabled:cursor-not-allowed"
            title={`Pull live BBG SAPI option chain for ${symbol} right now. ~15-25s. Merges into existing options-chain.json — other symbols are not touched.`}
          >
            {refreshingChain ? 'refreshing…' : '↻ refresh chain'}
          </button>
          <span className="text-[9px] text-[#64748b] uppercase tracking-wider">deployment view</span>
        </div>
      </header>
      {chainRefreshError && (
        <div className="text-[10px] text-red-400 -mt-2 pb-1">Chain refresh failed: {chainRefreshError}</div>
      )}

      {/* Position size + hedge sizing controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#0a0e14] border border-[#1e293b] rounded p-3">
          <label className="text-[9px] uppercase tracking-wider text-[#94a3b8] block mb-1 flex items-baseline justify-between gap-2">
            <span>Position size</span>
            {realShares != null && shares !== realShares && (
              <button
                type="button"
                onClick={() => { setShares(realShares); setTouched(false); }}
                className="text-[9px] uppercase tracking-wider text-amber-300 hover:text-amber-200 normal-case"
                title={`Reset to real holding from portfolio.json (${realShares.toLocaleString()} shares = normalised × $${(portfolio?.bookNav ?? 0).toLocaleString()} NAV)`}
              >
                use my position
              </button>
            )}
          </label>
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              value={shares}
              onChange={(e) => { setShares(Math.max(0, parseFloat(e.target.value) || 0)); setTouched(true); }}
              className="bg-[#0a0e14] border border-[#334155] focus:border-amber-500/60 focus:outline-none rounded px-2 py-1 text-[12px] tabular-nums text-white w-[120px]"
            />
            <span className="text-[10px] text-[#64748b]">shares</span>
            {realShares != null && shares === realShares && (
              <span className="text-[9px] text-emerald-300/70 uppercase tracking-wider" title={`Sourced from portfolio.json (${realShares.toLocaleString()} shares = ${(portfolio?.shares?.[symbol] ?? 0).toExponential(2)} × $${(portfolio?.bookNav ?? 0).toLocaleString()} NAV)`}>
                · your position
              </span>
            )}
          </div>
          <div className="text-[10px] text-[#64748b] mt-1 tabular-nums">
            Notional {fmtUsdNoSign(shares * spot)}
          </div>
        </div>

        <div className="bg-[#0a0e14] border border-[#1e293b] rounded p-3">
          <label className="text-[9px] uppercase tracking-wider text-[#94a3b8] block mb-1">Hedge ratio</label>
          <div className="flex items-baseline gap-2">
            <input
              type="range"
              min="0"
              max="1.5"
              step="0.05"
              value={hedgeRatio}
              onChange={(e) => setHedgeRatio(parseFloat(e.target.value))}
              className="flex-1 accent-amber-400"
            />
            <span className="text-[12px] tabular-nums text-amber-300 font-semibold w-[44px] text-right">{(hedgeRatio * 100).toFixed(0)}%</span>
          </div>
          <div className="text-[10px] text-[#64748b] mt-1 tabular-nums">
            {contracts} contracts each leg · 1:{(hedgeRatio).toFixed(2)} coverage
          </div>
        </div>

        <div className="bg-[#0a0e14] border border-[#1e293b] rounded p-3">
          <label className="text-[9px] uppercase tracking-wider text-[#94a3b8] block mb-1">Mode</label>
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              onClick={() => { setEventDriven(false); setHedgeRatio(recommendation.ratio); }}
              className="text-[10px] px-2 py-1 rounded border border-[#334155] text-[#cbd5e1] hover:border-amber-500/60 hover:text-amber-300"
              title={recommendation.rationale}
            >
              Tax-aware ({(recommendation.ratio * 100).toFixed(0)}%)
            </button>
            <button
              type="button"
              onClick={() => { setEventDriven(true); setHedgeRatio(1.0); }}
              className="text-[10px] px-2 py-1 rounded border border-[#334155] text-[#cbd5e1] hover:border-amber-500/60 hover:text-amber-300"
              title="Event-driven hedge — short tenor, full coverage"
            >
              Tactical (100%)
            </button>
            {neutralContracts != null && (
              <button
                type="button"
                onClick={() => setHedgeRatio(neutralContracts * CONTRACT_MULTIPLIER / Math.max(shares, 1))}
                className="text-[10px] px-2 py-1 rounded border border-[#334155] text-[#cbd5e1] hover:border-red-500/60 hover:text-red-300"
                title="Solve for delta-neutral — over-hedges each leg"
              >
                Δ-neutral ({neutralContracts.toFixed(0)})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TRADE TICKET — the actual orders to place. Shows bid AND ask per
          leg so the user can sanity-check spread cost. Execution convention:
          short = lift the BID (what you'd receive); long = hit the ASK (what
          you'd pay). The "@ $X" column shows the conservative execution side. */}
      <TradeTicket
        symbol={symbol}
        contracts={contracts}
        callLeg={callLeg}
        putLeg={putLeg}
        callStrike={callStrike}
        putStrike={putStrike}
        callPriceUnit={callPriceUnit}
        putPriceUnit={putPriceUnit}
        callPremium={callPremium}
        putCost={putCost}
        netPremium={netPremium}
        hedgeRatio={hedgeRatio}
        shares={shares}
        fmtUsd={fmtUsd}
      />

      {/* Canadian tax assessment — §80.6 ITA Synthetic Disposition Arrangement
          + capital-gains 50% inclusion. CRA framework, not IRS. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
        <div className={`border rounded p-3 ${
          cs?.risk === 'high' ? 'border-red-500/40 bg-red-500/5'
          : cs?.risk === 'medium' ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-emerald-500/30 bg-emerald-500/5'
        }`}>
          <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-1">§80.6 ITA Synthetic Disposition</div>
          <div className={`font-semibold text-[12px] mb-1 ${
            cs?.risk === 'high' ? 'text-red-300'
            : cs?.risk === 'medium' ? 'text-amber-300'
            : 'text-emerald-300'
          }`}>
            {cs?.risk === 'high' ? 'HIGH RISK' : cs?.risk === 'medium' ? 'MEDIUM' : 'LOW (safe harbor)'}
          </div>
          <div className="text-[10px] text-[#cbd5e1] leading-relaxed">{cs?.rationale}</div>
          {daysToExpiry < 365 && (
            <div className="text-[9px] text-emerald-300 mt-1.5 italic">
              ✓ {daysToExpiry}-day tenor &lt; 1 year — SDA rules don&apos;t apply regardless of width.
            </div>
          )}
        </div>
        <div className="border border-[#334155] bg-[#0a0e14] rounded p-3">
          <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-1">Capital Gains Treatment</div>
          <div className="font-semibold text-[12px] mb-1 text-amber-300">
            50% INCLUSION (Canadian)
          </div>
          <div className="text-[10px] text-[#cbd5e1] leading-relaxed">{canadianTax?.rationale}</div>
          {canadianTax?.itmCallNote && (
            <div className="text-[9px] text-amber-300 mt-1.5 italic">⚠ {canadianTax.itmCallNote}</div>
          )}
        </div>
      </div>

      {/* Position-level Greeks panel */}
      <div className="border border-[#1e293b] rounded p-3 bg-[#0a0e14]">
        <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-2">Position Greeks (post-hedge)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
          <GreekCell label="Net Δ" value={fmtDelta(greeks.total.shareDelta)} sub={`${fmtSignedPct(greeks.total.shareDelta / shares)} of long`} tone={greeks.total.shareDelta > shares * 0.7 ? 'amber' : greeks.total.shareDelta > 0 ? 'green' : 'red'} />
          <GreekCell label="Γ per 1% spot" value={fmtDelta(greeks.total.pctGamma)} sub={greeks.total.shareGamma < 0 ? 'short gamma — bleeds on big moves' : 'long gamma — pays on big moves'} tone={greeks.total.shareGamma < 0 ? 'red' : 'green'} />
          <GreekCell label="$ Vega / 1pp IV" value={fmtUsd(greeks.total.dollarVega)} sub={greeks.total.dollarVega < 0 ? 'short vega — hurt by IV spike' : 'long vega — helped by IV spike'} tone={greeks.total.dollarVega < 0 ? 'red' : 'green'} />
          <GreekCell label="$ Theta / day" value={fmtUsd(greeks.total.dollarTheta)} sub={greeks.total.dollarTheta > 0 ? 'collect decay' : 'pay decay'} tone={greeks.total.dollarTheta > 0 ? 'green' : 'red'} />
        </div>
      </div>

      {/* Premium + max gain/loss in $ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
        <SummaryCell label="Call premium $" value={fmtUsdNoSign(callPremium)} tone="green" />
        <SummaryCell label="Put cost $" value={fmtUsdNoSign(putCost)} tone="red" />
        <SummaryCell label="Net premium" value={fmtUsd(netPremium)} tone={netPremium >= 0 ? 'green' : 'red'} />
        <SummaryCell label="Floor / Ceiling" value={`$${putStrike} / $${callStrike}`} tone="amber" />
      </div>

      {/* Delta drift table */}
      <div className="border border-[#1e293b] rounded p-3 bg-[#0a0e14]">
        <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-2">
          Delta drift — how net Δ shifts as spot moves
          <span className="text-[#475569] normal-case ml-2">(linear gamma extrapolation; expect error past ±15%)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] tabular-nums">
            <thead className="text-[#64748b] uppercase tracking-wider text-[8px]">
              <tr><th className="text-left">Spot</th>{drift.map(d => <th key={d.spotOffset} className="text-right pl-3">{d.spotOffset === 0 ? 'now' : (d.spotOffset > 0 ? '+' : '') + (d.spotOffset * 100).toFixed(0) + '%'}</th>)}</tr>
            </thead>
            <tbody>
              <tr className="text-[#cbd5e1]">
                <td>${'  '}</td>
                {drift.map(d => <td key={d.spotOffset} className="text-right pl-3">${d.spot.toFixed(0)}</td>)}
              </tr>
              <tr className="text-amber-300 font-semibold">
                <td>Δ</td>
                {drift.map(d => <td key={d.spotOffset} className="text-right pl-3">{fmtDelta(d.shareDelta)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Stress matrix */}
      <StressMatrix stress={stress} fmtUsd={fmtUsd} />

      {/* Roll triggers */}
      <div className="border border-[#1e293b] rounded p-3 bg-[#0a0e14]">
        <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-2">Roll triggers</div>
        <div className="space-y-1.5 text-[10px]">
          {triggers.map(t => (
            <div key={t.key} className={`flex items-start gap-2 ${t.fired ? 'text-amber-200' : 'text-[#94a3b8]'}`}>
              <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${t.fired ? 'bg-amber-400' : 'bg-[#334155]'}`} />
              <div className="flex-1">
                <span className="font-semibold">{t.name}:</span> {t.rationale}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sizing recommendation */}
      <div className="border border-amber-500/30 rounded bg-amber-500/5 p-3 text-[11px] text-amber-200 leading-relaxed">
        <strong className="text-amber-300 uppercase tracking-wider text-[9px] block mb-1">Sizing recommendation</strong>
        {recommendation.rationale}
      </div>
    </section>
  );
}

